import { AnimatePresence, LayoutGroup, motion, useAnimation } from 'framer-motion';
import { useCallback, useEffect, useMemo } from 'react';
import { animationsConfig } from '../config/animationsConfig';
import { cookiesService } from '../services/cookiesService';
import { useChatStore } from '../store/chatStore';
import { useConfigStore } from '../store/configStore';
import { AvailableLanguage } from '@/types';
import { translateConfigValue } from '@/utils/translateConfigValue';
import { Button } from './ui/button';

const GDPR_TEXTS = {
  en: {
    title: '1648.AI Chatbot',
    description:
      'In order to use this chatbot, it is necessary to forward the data you enter to our service provider 1648.AI for processing.',
    privacyText: 'Further information on how we handle your personal data can be found in our',
    privacyLink: 'Privacy Policy',
    accept: 'Accept',
    reject: 'Reject',
  },
  de: {
    title: '1648.AI Chatbot',
    description:
      'Für die Nutzung dieses Chatbots ist es erforderlich, die von Ihnen eingegebenen Daten an unseren Dienstleister 1648.AI weiterzuleiten und dort zu verarbeiten.',
    privacyText:
      'Weitere Informationen zum Umgang mit Ihren personenbezogenen Daten finden Sie in unserer',
    privacyLink: 'Datenschutzerklärung',
    accept: 'Akzeptieren',
    reject: 'Ablehnen',
  },
} as const;

const DEFAULT_PRIVACY_POLICY_URL = 'https://www.1648.group/privacy-policy';

function getDefaultGdprCopy(language: AvailableLanguage) {
  return language === 'de' ? GDPR_TEXTS.de : GDPR_TEXTS.en;
}

const GDPR = () => {
  const config = useConfigStore(state => state.config);
  const gdprConfig = config.ui?.gdpr;
  const { isGdprOpen, setGdprOpen, setOpen, gdprShakeTrigger } = useChatStore();
  const controls = useAnimation();

  // Generate shake keyframes based on config
  const generateShakeKeyframes = useCallback(() => {
    const { intensity, oscillations } = animationsConfig.gdpr.shake;
    const keyframes = [];

    // Generate smooth oscillating motion
    for (let i = 0; i <= oscillations; i++) {
      const progress = i / oscillations;
      const amplitude = Math.sin(progress * Math.PI) * intensity;
      keyframes.push(amplitude);
    }

    return keyframes;
  }, []);

  // Shake animation function
  const shakeAnimation = useCallback(async () => {
    const keyframes = generateShakeKeyframes();

    // Add outline and ensure position
    await controls.start({
      x: 0,
      y: 0,
      opacity: 1,
      transition: { duration: 0 },
    });

    // Shake animation
    await controls.start({
      x: keyframes,
      y: 0,
      opacity: 1,
      transition: {
        duration: animationsConfig.gdpr.shake.duration,
        ease: 'easeInOut',
        times: keyframes.map((_, i) => i / (keyframes.length - 1)),
      },
    });

    // Remove outline with a fade
    await controls.start({
      x: 0,
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
      },
    });
  }, [controls, generateShakeKeyframes]);

  // Handle shake trigger
  useEffect(() => {
    if (isGdprOpen && gdprShakeTrigger > 0) {
      shakeAnimation();
    }
  }, [gdprShakeTrigger, isGdprOpen, shakeAnimation]);

  const handleAccept = () => {
    cookiesService.acceptAll();
    setGdprOpen(false);
    setOpen(true);
  };

  const handleReject = () => {
    cookiesService.rejectAll();
    setGdprOpen(false);
  };

  const language = (config.language ?? 'de') as AvailableLanguage;

  const texts = useMemo(() => {
    const defaults = getDefaultGdprCopy(language);
    const clientName = translateConfigValue(gdprConfig?.clientName, language);
    const titleFromClient =
      clientName && clientName.length > 0 ? `${clientName} Chatbot` : undefined;

    return {
      title: translateConfigValue(gdprConfig?.title, language) ?? titleFromClient ?? defaults.title,
      description: translateConfigValue(gdprConfig?.description, language) ?? defaults.description,
      privacyText: translateConfigValue(gdprConfig?.privacyText, language) ?? defaults.privacyText,
      privacyLink: translateConfigValue(gdprConfig?.privacyLink, language) ?? defaults.privacyLink,
      accept: translateConfigValue(gdprConfig?.accept, language) ?? defaults.accept,
      reject: translateConfigValue(gdprConfig?.reject, language) ?? defaults.reject,
      privacyPolicyUrl: gdprConfig?.privacyPolicyUrl ?? DEFAULT_PRIVACY_POLICY_URL,
    };
  }, [gdprConfig, language]);

  if (!gdprConfig?.enabled) return null;

  return (
    <LayoutGroup>
      <AnimatePresence mode="wait">
        {isGdprOpen && (
          <motion.div
            initial={{ opacity: 0, y: animationsConfig.gdpr.slideDistance }}
            animate={controls}
            exit={{ opacity: 0, y: animationsConfig.gdpr.slideDistance }}
            transition={{
              duration: animationsConfig.gdpr.appearDuration,
              ease: 'easeOut',
            }}
            layout
            className="pointer-events-auto absolute bottom-[20px] left-[20px] z-50 box-border w-full max-w-[351px] rounded-lg bg-white px-[20px] py-[16px] shadow-lg"
          >
            <motion.div layout className="flex flex-col">
              <motion.h3 layout className="mb-[8px] text-sm font-semibold text-widget-text-primary">
                {texts.title}
              </motion.h3>
              <motion.p
                layout
                className="mb-[16px] whitespace-pre-line text-sm text-widget-text-secondary"
              >
                {texts.description}
                <br />
                <br />
                {texts.privacyText}{' '}
                <a
                  href={texts.privacyPolicyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-widget-accent-500 hover:text-widget-accent-600"
                >
                  {texts.privacyLink}
                </a>
              </motion.p>
              <motion.div
                key="buttons"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.2 }}
                className="flex justify-end gap-3"
                layout
              >
                <Button variant="outline" size="sm" onClick={handleReject}>
                  {texts.reject}
                </Button>
                <Button variant="default" size="sm" onClick={handleAccept}>
                  {texts.accept}
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </LayoutGroup>
  );
};

export default GDPR;
